import React, { useState } from "react";
import { LandTourDetail } from "../../../../interfaces/land-tour.interface";
import DownloadItineraryButton from "./DownloadItineraryButton";

interface Props {
  landTour: LandTourDetail;
}

// Day-by-day timeline. Modelled on pages/gd/ItineraryTab.tsx's data shape
// (day/title/description/cities/activities) but written lean - the Figma card
// here is simpler than the series stepper (no image carousel per day).
export default function GroundTourItinerary({ landTour }: Props) {
  const { itinerary } = landTour;
  // Empty by default so every day starts expanded.
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  if (!itinerary.length) return null;

  const toggleDay = (index: number) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <section className="ground-tour-show-v2__itinerary">
      <div className="ground-tour-show-v2__itinerary-header">
        <h2 className="ground-tour-show-v2__section-title">Itinerary</h2>
        <p>Final sequences of the itinerary is subject to local ground final arrangement.</p>

        <DownloadItineraryButton landTour={landTour} />
      </div>

      <div className="ground-tour-show-v2__day-list">
        {itinerary.map((day, index) => {
          const isExpanded = !collapsed.has(index);

          return (
            <div className="ground-tour-show-v2__day-card" key={index}>
              <span className="ground-tour-show-v2__day-pill">{day.day_no}</span>

              <div className="ground-tour-show-v2__day-content">
                <div className="ground-tour-show-v2__day-title-row">
                  <h3>{day.title}</h3>

                  <div className="ground-tour-show-v2__day-actions">
                    {day.inclusion && (
                      <span className="ground-tour-show-v2__day-meals">
                        <span>{day.inclusion}</span>
                      </span>
                    )}

                    <button
                      type="button"
                      className="ground-tour-show-v2__day-toggle"
                      onClick={() => toggleDay(index)}
                      aria-label={isExpanded ? "Collapse day" : "Expand day"}
                      aria-expanded={isExpanded}
                    >
                      <img
                        className={isExpanded ? "" : "is-collapsed"}
                        src="/iceholidays-assets/images/icons/icon-chevron-up.png"
                        alt=""
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <>
                    {day.description && <p className="ground-tour-show-v2__day-description">{day.description}</p>}

                    {day.cities.length > 0 && (
                      <div className="ground-tour-show-v2__day-cities">
                        <span className="ground-tour-show-v2__day-cities-label">Cities</span>
                        {day.cities.map((city, cityIndex) => (
                          <div className="ground-tour-show-v2__day-city" key={cityIndex}>
                            <span className="ground-tour-show-v2__day-city-dot" />
                            {city.city}
                          </div>
                        ))}
                      </div>
                    )}

                    {day.activities?.length > 0 && (
                      <div className="ground-tour-show-v2__day-activities">
                        <span className="ground-tour-show-v2__day-cities-label">Activities</span>
                        {day.activities.map((activity, activityIndex) => (
                          <div className="ground-tour-show-v2__day-city" key={activityIndex}>
                            <span className="ground-tour-show-v2__day-city-dot" />
                            {activity.activity}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
